import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  IndianRupee, Plus, Minus, RotateCcw, Save, Search, TrendingUp, TrendingDown, LayoutDashboard, LogOut, Tag, ArrowLeft, Percent, ArrowUp, ArrowDown,
} from "lucide-react";
import PMRLogo from "../components/PMRLogo";
import { PACKAGES, HOTELS, FLIGHTS, BUS_ROUTES, CAB_TYPES, POPULAR_TRAINS } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const STORAGE = "pmr_price_overrides";

const getOverrides = () => {
  const raw = localStorage.getItem(STORAGE);
  if (!raw) return {};
  try { return JSON.parse(raw); } catch { return {}; }
};

const PriceRow = ({ id, name, subtitle, basePrice, override, onSet, onDelta, currency = true }) => {
  const current = override ?? basePrice;
  const diff = current - basePrice;
  const pct = basePrice ? ((diff / basePrice) * 100).toFixed(1) : 0;

  return (
    <div className="grid grid-cols-[1fr_auto] md:grid-cols-[1.6fr_1fr_auto_auto] gap-3 items-center px-5 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
      <div>
        <div className="font-semibold text-slate-900 text-sm">{name}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </div>
      <div className="flex items-center gap-1 md:gap-2">
        <button
          onClick={() => onDelta(-100)}
          className="w-7 h-7 rounded-md bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center"
          title="Decrease by 100"
        >
          <Minus className="w-3 h-3" />
        </button>
        <div className="relative flex-1 max-w-[130px]">
          {currency && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-bold">₹</span>}
          <Input
            type="number"
            value={current}
            onChange={(e) => onSet(parseFloat(e.target.value) || 0)}
            className={`h-8 ${currency ? "pl-5" : ""} text-right font-semibold`}
          />
        </div>
        <button
          onClick={() => onDelta(100)}
          className="w-7 h-7 rounded-md bg-green-50 text-green-700 hover:bg-green-100 flex items-center justify-center"
          title="Increase by 100"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
      <div className="text-right hidden md:block">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">Base</div>
        <div className="text-sm text-slate-600 font-mono">₹{basePrice.toLocaleString("en-IN")}</div>
      </div>
      <div className="text-right">
        {diff !== 0 ? (
          <div className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full ${diff > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
            {diff > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {diff > 0 ? "+" : ""}{pct}%
          </div>
        ) : (
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">unchanged</div>
        )}
      </div>
    </div>
  );
};

const CATEGORIES = {
  packages: { label: "Tour Packages", items: PACKAGES, priceKey: "price", nameKey: "title", subKey: (it) => `${it.destination} · ${it.nights}N/${it.days}D` },
  hotels: { label: "Hotels", items: HOTELS, priceKey: "price", nameKey: "name", subKey: (it) => `${it.city} · ${it.stars}★ per night` },
  flights: { label: "Flights", items: FLIGHTS, priceKey: "price", nameKey: "airline", subKey: (it) => `${it.code} · ${it.from} → ${it.to}` },
  buses: { label: "Buses", items: BUS_ROUTES, priceKey: "price", nameKey: "operator", subKey: (it) => `${it.from} → ${it.to} · ${it.type}` },
  cabs: { label: "Cabs / Cars", items: CAB_TYPES, priceKey: "base", nameKey: "name", subKey: (it) => `${it.type} · ${it.seats} seats · base fare` },
  trains: { label: "Trains", items: POPULAR_TRAINS, priceKey: "fromPrice", nameKey: "name", subKey: (it) => `#${it.no} · ${it.from} → ${it.to}` },
};

const AdminPricingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [overrides, setOverrides] = useState(getOverrides());
  const [query, setQuery] = useState("");
  const [bulkPct, setBulkPct] = useState(5);

  if (!localStorage.getItem("pmr_admin")) {
    return <Navigate to="/admin/login" replace />;
  }

  const setOverride = (cat, id, value) => {
    const next = { ...overrides, [`${cat}:${id}`]: value };
    setOverrides(next);
  };

  const clearOverride = (cat, id) => {
    const next = { ...overrides };
    delete next[`${cat}:${id}`];
    setOverrides(next);
  };

  const save = () => {
    localStorage.setItem(STORAGE, JSON.stringify(overrides));
    toast({ title: "Prices saved", description: `${Object.keys(overrides).length} override(s) persisted.` });
  };

  const resetAll = () => {
    setOverrides({});
    localStorage.removeItem(STORAGE);
    toast({ title: "All prices reset", description: "Back to base prices." });
  };

  const bulkAdjust = (cat, direction) => {
    const items = CATEGORIES[cat].items;
    const key = CATEGORIES[cat].priceKey;
    const next = { ...overrides };
    items.forEach((it) => {
      const base = it[key];
      const current = next[`${cat}:${it.id || it.no || it.code}`] ?? base;
      const delta = current * (bulkPct / 100) * (direction === "up" ? 1 : -1);
      next[`${cat}:${it.id || it.no || it.code}`] = Math.max(0, Math.round(current + delta));
    });
    setOverrides(next);
    toast({
      title: `${CATEGORIES[cat].label} ${direction === "up" ? "increased" : "decreased"} by ${bulkPct}%`,
      description: "Click Save to persist changes.",
    });
  };

  const overrideCount = Object.keys(overrides).length;

  const logout = () => {
    localStorage.removeItem("pmr_admin");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <aside className="fixed inset-y-0 left-0 w-64 bg-slate-950 text-white p-6 hidden lg:flex flex-col">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
            <PMRLogo size={28} color="#0f766e" accent="#f97316" />
          </div>
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>PMR Admin</div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400">Console</div>
          </div>
        </Link>

        <nav className="mt-8 space-y-1 flex-1">
          <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 hover:text-white text-sm">
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </Link>
          <a className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/10 text-white font-medium text-sm">
            <Tag className="w-4 h-4" /> Price Management
          </a>
        </nav>

        <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-red-500/10 hover:text-red-300 text-sm">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </aside>

      <main className="lg:ml-64">
        <div className="sticky top-0 bg-white border-b border-slate-200 z-10">
          <div className="px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button asChild variant="ghost" size="sm" className="gap-1">
                <Link to="/admin"><ArrowLeft className="w-4 h-4" /> Back</Link>
              </Button>
              <div>
                <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Price Management</h1>
                <p className="text-xs text-slate-500">Adjust prices up/down across all bookable products</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {overrideCount > 0 && (
                <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 border-orange-200">
                  {overrideCount} unsaved change{overrideCount > 1 ? "s" : ""}
                </Badge>
              )}
              <Button variant="outline" onClick={resetAll} className="gap-1"><RotateCcw className="w-4 h-4" /> Reset All</Button>
              <Button onClick={save} className="bg-teal-600 hover:bg-teal-700 gap-1"><Save className="w-4 h-4" /> Save Changes</Button>
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          <Tabs defaultValue="packages">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
              <TabsList className="bg-white border border-slate-200 h-11 flex-wrap">
                {Object.entries(CATEGORIES).map(([key, c]) => (
                  <TabsTrigger key={key} value={key} className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
                    {c.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="relative max-w-xs w-full">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search item..." className="pl-9 h-10" />
              </div>
            </div>

            {Object.entries(CATEGORIES).map(([cat, config]) => {
              const items = config.items.filter((it) => {
                if (!query) return true;
                const name = String(it[config.nameKey] || "").toLowerCase();
                return name.includes(query.toLowerCase()) || config.subKey(it).toLowerCase().includes(query.toLowerCase());
              });

              return (
                <TabsContent key={cat} value={cat} className="mt-4">
                  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-slate-900">{config.label}</h3>
                        <p className="text-xs text-slate-500">{items.length} items · use +/- buttons or type a new price</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Percent className="w-3.5 h-3.5 text-slate-500" />
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            value={bulkPct}
                            onChange={(e) => setBulkPct(parseFloat(e.target.value) || 0)}
                            className="h-8 w-16 text-right font-semibold"
                          />
                        </div>
                        <Button size="sm" variant="outline" onClick={() => bulkAdjust(cat, "down")} className="gap-1 border-red-200 text-red-700 hover:bg-red-50">
                          <TrendingDown className="w-3.5 h-3.5" /> Bulk Down
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => bulkAdjust(cat, "up")} className="gap-1 border-green-200 text-green-700 hover:bg-green-50">
                          <TrendingUp className="w-3.5 h-3.5" /> Bulk Up
                        </Button>
                      </div>
                    </div>

                    <div>
                      {items.length === 0 && (
                        <div className="p-8 text-center text-sm text-slate-500">No items match your search.</div>
                      )}
                      {items.map((it) => {
                        const id = it.id || it.no || it.code;
                        const base = it[config.priceKey];
                        const key = `${cat}:${id}`;
                        return (
                          <PriceRow
                            key={key}
                            id={id}
                            name={it[config.nameKey]}
                            subtitle={config.subKey(it)}
                            basePrice={base}
                            override={overrides[key]}
                            onSet={(v) => setOverride(cat, id, v)}
                            onDelta={(d) => {
                              const current = overrides[key] ?? base;
                              setOverride(cat, id, Math.max(0, current + d));
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>

          <div className="rounded-xl bg-teal-50 border border-teal-100 p-4 flex items-start gap-3">
            <IndianRupee className="w-5 h-5 text-teal-700 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">
              <div className="font-semibold text-slate-900">How pricing works</div>
              Prices set here are saved in your admin browser (localStorage) for demo purposes. In production, these should sync to a backend so all customers see the updated fares. Base prices in code stay unchanged — use "Reset All" to remove overrides.
            </div>
          </div>

          <p className="text-center text-[11px] text-orange-600 font-semibold pb-6">
            DEMO MODE — Price changes are stored locally and don't reflect on customer-facing pages yet.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AdminPricingPage;
